/* eslint-disable indent */
import {
  Grid,
  makeStyles,
  TextField,
  Tooltip,
  IconButton,
  InputAdornment,
} from '@material-ui/core';
import { Lock } from '@material-ui/icons';
import clsx from 'clsx';
import ToggleCard from 'components/ToggleCard';
import Button from 'components/Button';
import InfoOutlinedIcon from '@material-ui/icons/InfoOutlined';
import AutorenewIcon from '@material-ui/icons/Autorenew';
import FileCopyIcon from '@material-ui/icons/FileCopy';
import { messagesRequest } from 'modules/messages/actions';
import i18n from 'i18n';
import VisibilityIcon from '@material-ui/icons/Visibility';
import VisibilityOffIcon from '@material-ui/icons/VisibilityOff';
import Permission from 'components/ValidPermission';
import { CompanyData, RolesEnum } from 'modules/authenticate/interfaces';
import {
  DeviceValidationSettings,
  EnvironmentFields,
} from 'modules/environments/interfaces';
import React from 'react';
import { connect } from 'react-redux';
import ApplicationState from 'store/interfaces';
import { inAdminView, inOperatorView, inSuperAdminView } from 'utils/role';
import { resetAdminPinRequest } from 'modules/environments/actions';
import styles from '../styles';

const useStyles = makeStyles(styles);

interface DeviceValidationCardProps {
  companySelected: CompanyData | null;
  environmentSelected: EnvironmentFields | null;
  role?: RolesEnum;
  deviceValidationValues?: DeviceValidationSettings;
  messagesRequest: (props) => void;
  setFieldValue: (field: string, value: any) => void;
  resetAdminPinRequest: (props) => void;
}

const DeviceValidationCard = ({
  companySelected,
  environmentSelected,
  role,
  deviceValidationValues,
  messagesRequest,
  setFieldValue,
  resetAdminPinRequest,
}: DeviceValidationCardProps) => {
  const classes = useStyles();
  const [showPin, setShowPin] = React.useState(false);

  const handleResetPin = () => {
    resetAdminPinRequest({
      environmentUuid: environmentSelected?.environment_uuid,
    });
  };

  return (
    <ToggleCard
      id="toggle-device-validation"
      active={!!deviceValidationValues?.device_validation_enabled}
      title={
        <div className={classes.titleAndIcon}>
          {i18n.t('SECURE_DEVICE_VALIDATION_KEY')}
          <Tooltip title={i18n.t('SECURE_DEVICE_VALIDATION_TOOLTIP')} arrow>
            <InfoOutlinedIcon fontSize="small" color="action" />
          </Tooltip>
        </div>
      }
      icon={
        <Lock
          className={clsx(classes.iconLock, {
            [classes.iconLockAdmin]: inAdminView(role, companySelected),
            [classes.iconLockSuperAdmin]: inSuperAdminView(
              role,
              companySelected
            ),
            [classes.iconLockOperator]: inOperatorView(role, companySelected),
          })}
        />
      }
      getActive={value =>
        setFieldValue(
          'device_validation_settings.device_validation_enabled',
          !value
        )
      }
    >
      <section
        className={
          !!deviceValidationValues?.device_validation_enabled &&
          !!deviceValidationValues?.environment_admin_pin
            ? classes.content
            : ''
        }
      >
        <Grid container spacing={2}>
          {!!deviceValidationValues?.device_validation_enabled &&
            !!deviceValidationValues?.environment_admin_pin && (
              <Grid
                container
                spacing={2}
                alignItems="center"
                className={classes.currentPinGrid}
              >
                <Grid item xs={6} className={classes.currentPin}>
                  <TextField
                    label={i18n.t('ADMIN_PIN_KEY')}
                    value={deviceValidationValues.environment_admin_pin}
                    fullWidth
                    variant="outlined"
                    type={showPin ? 'text' : 'password'}
                    inputProps={{
                      maxLength: 6,
                      inputMode: 'numeric',
                      pattern: '[0-9]*',
                      style: {
                        fontSize: 28,
                        letterSpacing: '0.5em',
                        textAlign: 'center',
                      },
                    }}
                    InputLabelProps={{ shrink: true }}
                    InputProps={{
                      endAdornment: (
                        <InputAdornment position="end">
                          <IconButton
                            aria-label="toggle pin visibility"
                            onClick={() => setShowPin(prev => !prev)}
                            edge="end"
                          >
                            {showPin ? (
                              <VisibilityOffIcon />
                            ) : (
                              <VisibilityIcon />
                            )}
                          </IconButton>
                        </InputAdornment>
                      ),
                    }}
                    disabled
                  />
                </Grid>

                <Grid item container xs={4} spacing={1} alignItems="center">
                  <Grid item>
                    <Tooltip arrow title={i18n.t('COPY_PIN_KEY')}>
                      <IconButton
                        aria-label="copy"
                        onClick={() => {
                          navigator.clipboard.writeText(
                            deviceValidationValues.environment_admin_pin
                          );
                          messagesRequest({
                            type: 'success',
                            message: i18n.t('PIN_COPIED_KEY'),
                          });
                        }}
                      >
                        <FileCopyIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                  </Grid>

                  <Permission value="reset_administrative_pin">
                    <Grid item>
                      <Button
                        variant="text"
                        size="small"
                        startIcon={<AutorenewIcon fontSize="small" />}
                        onClick={() => {
                          handleResetPin();
                        }}
                      >
                        {i18n.t('REGENERATE_PIN_KEY')}
                      </Button>
                    </Grid>
                  </Permission>
                </Grid>
              </Grid>
            )}
        </Grid>
      </section>
    </ToggleCard>
  );
};

const mapStateToProps = ({
  environments: { environmentSelected },
  authenticate: { companySelected, data },
}: ApplicationState) => ({
  environmentSelected,
  companySelected,
  role: data?.role,
});

export default connect(mapStateToProps, {
  resetAdminPinRequest,
  messagesRequest,
})(DeviceValidationCard);
